import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container
} from "@mui/material";
import { Search } from "@mui/icons-material";
import SharedNavbar from "./performancenavbar.jsx";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const EmployeeSuccessionPlan = () => {
  const [successionPlans, setSuccessionPlans] = useState([]);
  const [employeeIdInput, setEmployeeIdInput] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });

  const fetchEmployeePlans = async (employeeId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
    
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      setLoading(true);
      
      // Fixed: Remove duplicate /api/succession-plans since API_URL already includes it
      const response = await axios.get(
        `${API_URL}/api/succession-plans/employee/${employeeId}`, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true
        }
      );


      if (response.data && response.data.length > 0) {
        setSuccessionPlans(response.data);
      } else {
        setSuccessionPlans([]);
        showSnackbar("No succession plans found for this employee", "info");
      }
    } catch (error) {
      //console.error("Error fetching plans:", error);
      showSnackbar(
        error.response?.data?.message || error.response?.data?.error || "Error fetching plans", 
        "error"
      );
      setSuccessionPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/user/current`, {
          withCredentials: true
        });


        if (response.data && response.data.employee_id) {
          setEmployeeIdInput(response.data.employee_id);
          setSelectedEmployee(response.data.employee_id);
          fetchEmployeePlans(response.data.employee_id);
        } else {
          showSnackbar("Could not retrieve employee ID", "warning");
        }

        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const company_id = payload.company_id || payload.company_registration_no;
          if (company_id) {
            try {
              const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (cmpRes.data && cmpRes.data.data) {
                setCompanyConfig({
                  dateFormat: cmpRes.data.data.dateFormat || 'DD/MM/YYYY',
                  timezone: cmpRes.data.data.timezone || 'Asia/Kolkata'
                });
              }
            } catch(e) {}
          }
        }
      } catch (error) {
        //console.error("Error fetching current user:", error);
        showSnackbar("Failed to fetch user information", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  const handleSearch = () => {
    if (employeeIdInput.trim()) {
      setSelectedEmployee(employeeIdInput.trim());
      fetchEmployeePlans(employeeIdInput.trim());
    } else {
      showSnackbar("Please enter an employee ID", "warning");
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return dayjs(dateString).tz(companyConfig.timezone).format(companyConfig.dateFormat);
    } catch (e) {
      return "Invalid Date";
    }
  };

  return (
    <>
      <SharedNavbar />
      <Container maxWidth="xl" sx={{ mt: 3 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Employee Succession Plan Search
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Employee ID"
                  value={employeeIdInput}
                  onChange={(e) => setEmployeeIdInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  sx={{ width: "300px" }}
                  disabled={loading}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  startIcon={<Search />}
                  disabled={loading}
                  sx={{ width: "120px", height: "56px" }}
                >
                  Search
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                mb: 2
              }}>
                <Typography variant="h6">
                  Succession Plans ({successionPlans.length})
                </Typography>
                {selectedEmployee && (
                  <Typography variant="body2" color="text.secondary">
                    Employee ID: {selectedEmployee}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 1 }} />

              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell><strong>Employee ID</strong></TableCell>
                      <TableCell><strong>Position</strong></TableCell>
                      <TableCell><strong>Readiness Level</strong></TableCell>
                      <TableCell><strong>Development Needs</strong></TableCell>
                      <TableCell><strong>Timeline</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Box sx={{ p: 2 }}>
                            <LinearProgress />
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Loading succession plans...
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : successionPlans.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="textSecondary" sx={{ py: 3 }}>
                            {employeeIdInput ?
                              `No succession plans found for employee ${employeeIdInput}` :
                              "Enter an employee ID to search"
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      successionPlans.map((plan) => (
                        <TableRow key={plan.id} hover>
                          <TableCell>{plan.employee_id}</TableCell>
                          <TableCell>{plan.position}</TableCell>
                          <TableCell>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '4px',
                              backgroundColor: 
                                plan.readinessLevel === 'Ready Now' ? '#e8f5e9' :
                                plan.readinessLevel === '0-6 months' ? '#fff3e0' :
                                plan.readinessLevel === '6-12 months' ? '#ffe0b2' :
                                '#ffebee',
                              color: 
                                plan.readinessLevel === 'Ready Now' ? '#2e7d32' :
                                plan.readinessLevel === '0-6 months' ? '#f57c00' :
                                plan.readinessLevel === '6-12 months' ? '#ef6c00' :
                                '#c62828',
                              fontWeight: '500'
                            }}>
                              {plan.readinessLevel}
                            </span>
                          </TableCell>
                          <TableCell>{plan.developmentNeeds}</TableCell>
                          <TableCell>{formatDate(plan.timeline)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Paper>

        {/* Snackbar for notifications */}
        {snackbar.open && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              bgcolor: 
                snackbar.severity === 'error' ? '#f44336' :
                snackbar.severity === 'warning' ? '#ff9800' :
                snackbar.severity === 'info' ? '#2196f3' :
                '#4caf50',
              color: 'white',
              p: 2,
              borderRadius: 1,
              boxShadow: 3,
              zIndex: 9999
            }}
          >
            <Typography>{snackbar.message}</Typography>
          </Box>
        )}
      </Container>
    </>
  );
};

export default EmployeeSuccessionPlan;